import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion } from 'motion/react';
import { Activity, TrendingUp, TrendingDown, ShoppingBag, DollarSign, BarChart3, X, Zap } from 'lucide-react';

interface AnalyticsData {
  store: string;
  currentMetrics: {
    totalRevenue: number;
    totalTransactions: number;
    averageOrderValue: number;
    topSellingCategory: string;
    currency: string;
  };
  historicalChartData: { date: string; revenue: number }[];
  forecastChartData: { date: string; predictedRevenue: number }[];
  growthTrend: string;
}

interface TrendsData {
  bestDay: string | null;
  worstDay: string | null;
  dayTotals: Record<string, number>;
  dayCounts: Record<string, number>;
  totalTransactions: number;
}

const BOOT_LINES = [
  '◈ Initializing Neural Analytics Core...',
  '◈ Fetching live transaction stream...',
  '◈ Calibrating forecast engine...',
  '◈ Rendering holographic interface...',
];

// ── Metric card — defined OUTSIDE the main component for stable identity ──
const MetricCard = React.memo(({ icon, label, value, sub, delay, accent = 'cyan' }: { icon: React.ReactNode; label: string; value: string; sub?: string; delay: number; accent?: string }) => (
  <motion.div
    initial={{ opacity: 0, y: 24, scale: 0.92 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ delay, type: 'spring', stiffness: 160, damping: 18 }}
    className={`relative bg-gradient-to-br from-${accent}-950/50 to-black/60 border border-${accent}-500/20 rounded-2xl p-4 flex flex-col gap-2 overflow-hidden group`}
  >
    <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl" />
    <div className="flex items-center gap-2 text-[9px] uppercase font-mono tracking-[0.2em] text-cyan-500/50">
      {icon}
      {label}
    </div>
    <p className="text-2xl sm:text-3xl font-bold text-white drop-shadow-[0_0_12px_rgba(34,211,238,0.5)] leading-none">{value}</p>
    {sub && <p className="text-[10px] font-mono text-slate-500">{sub}</p>}
    <div className="absolute bottom-0 left-0 w-full h-[2px] bg-gradient-to-r from-cyan-500/50 via-purple-500/30 to-transparent" />
  </motion.div>
));

// ── SVG Chart — stable component ──────────────────────────────────
const AnalyticsChart = React.memo(({ data }: { data: AnalyticsData }) => {
  const hist = data.historicalChartData;
  const fore = data.forecastChartData;
  const allRevs = [...hist.map(d => d.revenue), ...fore.map(d => d.predictedRevenue)];
  const maxVal = Math.max(...allRevs, 1);
  const totalPts = hist.length + fore.length;
  const W = 600, H = 180, PAD_X = 30, PAD_Y = 20;
  const usableW = W - PAD_X * 2;
  const usableH = H - PAD_Y * 2;

  const toX = (i: number) => PAD_X + (i / Math.max(totalPts - 1, 1)) * usableW;
  const toY = (v: number) => PAD_Y + usableH - (v / maxVal) * usableH;

  const histPts = hist.map((d, i) => `${toX(i)},${toY(d.revenue)}`).join(' ');
  const lastHistPt = `${toX(hist.length - 1)},${toY(hist[hist.length - 1]?.revenue || 0)}`;
  const forePts = fore.map((d, i) => `${toX(hist.length + i)},${toY(d.predictedRevenue)}`).join(' ');
  const foreFullPts = `${lastHistPt} ${forePts}`;
  const histArea = `M ${PAD_X},${PAD_Y + usableH} L ${histPts} L ${toX(hist.length - 1)},${PAD_Y + usableH} Z`;

  const foreAreaPts = fore.length > 0 ? [
    `${toX(hist.length - 1)},${PAD_Y + usableH}`,
    lastHistPt,
    ...fore.map((d, i) => `${toX(hist.length + i)},${toY(d.predictedRevenue)}`),
    `${toX(hist.length + fore.length - 1)},${PAD_Y + usableH}`,
  ].join(' ') : '';

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-full" preserveAspectRatio="xMidYMid meet">
      {[0.25, 0.5, 0.75, 1].map(frac => {
        const y = PAD_Y + usableH - frac * usableH;
        return <line key={frac} x1={PAD_X} x2={W - PAD_X} y1={y} y2={y} stroke="rgba(6,182,212,0.1)" strokeWidth={0.5} />;
      })}
      {[0, 0.5, 1].map(frac => {
        const y = PAD_Y + usableH - frac * usableH;
        const val = Math.round(maxVal * frac);
        const label = val >= 1000 ? `${(val / 1000).toFixed(0)}K` : String(val);
        return <text key={frac} x={PAD_X - 4} y={y + 3} textAnchor="end" fill="rgba(6,182,212,0.4)" fontSize={8} fontFamily="monospace">{label}</text>;
      })}
      <motion.path d={histArea} fill="url(#histGrad)" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.3 }} />
      <motion.polyline points={histPts} fill="none" stroke="rgba(6,182,212,0.9)" strokeWidth={2} strokeLinejoin="round" initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 1 }} transition={{ duration: 1.2, delay: 0.2 }} />
      {fore.length > 0 && (
        <motion.polyline points={foreFullPts} fill="none" stroke="rgba(139,92,246,0.8)" strokeWidth={2} strokeDasharray="6 4" strokeLinejoin="round" initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 1 }} transition={{ duration: 1, delay: 1.4 }} />
      )}
      {fore.length > 0 && (
        <motion.polygon points={foreAreaPts} fill="url(#foreGrad)" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 1.6 }} />
      )}
      <defs>
        <linearGradient id="histGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(6,182,212,0.25)" />
          <stop offset="100%" stopColor="rgba(6,182,212,0)" />
        </linearGradient>
        <linearGradient id="foreGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(139,92,246,0.15)" />
          <stop offset="100%" stopColor="rgba(139,92,246,0)" />
        </linearGradient>
      </defs>
      <line x1={W - 140} x2={W - 120} y1={10} y2={10} stroke="rgba(6,182,212,0.9)" strokeWidth={2} />
      <text x={W - 116} y={13} fill="rgba(6,182,212,0.7)" fontSize={8} fontFamily="monospace">Actual</text>
      <line x1={W - 70} x2={W - 50} y1={10} y2={10} stroke="rgba(139,92,246,0.8)" strokeWidth={2} strokeDasharray="4 3" />
      <text x={W - 46} y={13} fill="rgba(139,92,246,0.7)" fontSize={8} fontFamily="monospace">Forecast</text>
    </svg>
  );
});

// ── Weekly Trends bar chart — dark SVG, matches HologramDashboard aesthetic ──
const WeeklyTrendsBar = React.memo(({ data }: { data: TrendsData }) => {
  const DAY_FULL  = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  const DAY_SHORT = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  const entries = DAY_FULL.map((full, i) => ({
    label: DAY_SHORT[i],
    value: data.dayTotals[full] || 0,
    isBest:  full === data.bestDay,
    isWorst: full === data.worstDay,
  }));
  const maxVal  = Math.max(...entries.map(e => e.value), 1);
  const W = 600, H = 130, PAD_L = 18, PAD_R = 8, PAD_TOP = 22, PAD_BOT = 22;
  const usableW = W - PAD_L - PAD_R;
  const usableH = H - PAD_TOP - PAD_BOT;
  const slotW   = usableW / entries.length;
  const barW    = slotW * 0.52;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-full" preserveAspectRatio="xMidYMid meet">
      {[0.33, 0.66, 1].map(frac => {
        const y   = PAD_TOP + usableH - frac * usableH;
        const val = Math.round(maxVal * frac);
        const lbl = val >= 1000 ? `${(val/1000).toFixed(0)}K` : String(val);
        return (
          <g key={frac}>
            <line x1={PAD_L} x2={W - PAD_R} y1={y} y2={y} stroke="rgba(6,182,212,0.08)" strokeWidth={0.5}/>
            <text x={PAD_L - 2} y={y + 3} textAnchor="end" fill="rgba(6,182,212,0.3)" fontSize={7} fontFamily="monospace">{lbl}</text>
          </g>
        );
      })}
      {entries.map((e, i) => {
        const cx   = PAD_L + i * slotW + slotW / 2;
        const barH = Math.max((e.value / maxVal) * usableH, e.value > 0 ? 3 : 0);
        const y    = PAD_TOP + usableH - barH;
        const fill = e.isBest ? 'rgba(16,185,129,0.85)' : e.isWorst ? 'rgba(239,68,68,0.72)' : 'rgba(6,182,212,0.55)';
        return (
          <motion.g key={e.label} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55 + i * 0.07 }}>
            <rect x={cx - barW/2} y={y} width={barW} height={barH} fill={fill} rx={3}/>
            {e.isBest  && <text x={cx} y={y - 5} textAnchor="middle" fill="rgba(16,185,129,0.9)"  fontSize={7} fontFamily="monospace" fontWeight="bold">▲TOP</text>}
            {e.isWorst && <text x={cx} y={y - 5} textAnchor="middle" fill="rgba(239,68,68,0.85)" fontSize={7} fontFamily="monospace">▼LOW</text>}
            <text x={cx} y={PAD_TOP + usableH + 13} textAnchor="middle" fill="rgba(148,163,184,0.7)" fontSize={9} fontFamily="monospace">{e.label}</text>
          </motion.g>
        );
      })}
    </svg>
  );
});

export default function HologramDashboard({ apiKey, onClose, onDailySummary }: { apiKey: string; onClose: () => void; onDailySummary?: (data: { todaySalesCount: number; todayRevenue: number; totalAllTimeRevenue: number; todayNetProfit: number; totalNetProfit: number }) => void }) {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bootIdx, setBootIdx] = useState(0);
  const [booted, setBooted] = useState(false);
  const [trendsData, setTrendsData] = useState<TrendsData | null>(null);
  const [dailySummary, setDailySummary] = useState<{ todaySalesCount: number; todayRevenue: number; totalAllTimeRevenue: number; todayNetProfit: number; totalNetProfit: number } | null>(null);
  const hasFetched = useRef(false);

  // Propagate dashboard metrics to parent (LunaVoiceModel) whenever they load
  useEffect(() => {
    if (dailySummary && onDailySummary) onDailySummary(dailySummary);
  }, [dailySummary, onDailySummary]);

  // Boot sequence — runs exactly once
  useEffect(() => {
    let cancelled = false;
    const advance = (step: number) => {
      if (cancelled) return;
      if (step < BOOT_LINES.length) {
        setBootIdx(step);
        setTimeout(() => advance(step + 1), 400);
      } else {
        setTimeout(() => { if (!cancelled) setBooted(true); }, 300);
      }
    };
    advance(0);
    return () => { cancelled = true; };
  }, [setBootIdx, setBooted]);

  // Fetch real data — runs exactly once (guarded against StrictMode double-invoke)
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    const cashierToken = localStorage.getItem('kangavena:cashierToken') || '';
    // Send both apiKey + cashier JWT so the server can auth via whichever is present.
    // Handles the race condition where storeApiKey is still '' when cashier route
    // hasn't yet resolved /api/my-store (see App.tsx apiKey state init).
    const analyticsHeaders: Record<string, string> = {};
    if (apiKey) analyticsHeaders['x-store-api-key'] = apiKey;
    if (cashierToken) analyticsHeaders['Authorization'] = `Bearer ${cashierToken}`;
    (async () => {
      try {
        const res = await fetch('/api/luna/analytics', { headers: analyticsHeaders });
        if (!res.ok) throw new Error('Failed to load analytics');
        setData(await res.json());
      } catch (e: any) {
        setError(e.message || 'Analytics unavailable');
      } finally {
        setLoading(false);
      }
      // ── Daily summary SSOT (today's revenue/count + all-time revenue) ──
      try {
        const dsRes = await fetch('/api/reports/daily-summary', { headers: analyticsHeaders });
        if (dsRes.ok) setDailySummary(await dsRes.json());
      } catch { /* not critical */ }

      // ── Weekly trends (manager only — silently skip 403) ──────────────
      if (cashierToken) {
        try {
          const tRes = await fetch('/api/analytics/sales-trends', {
            headers: { Authorization: `Bearer ${cashierToken}` },
          });
          if (tRes.ok) setTrendsData(await tRes.json());
        } catch { /* not critical — trends panel simply won't render */ }
      }
    })();
  }, [setData, setTrendsData, setError, setBooted]);

  // Derived values — stable between renders when data hasn't changed
  const trendPositive = data?.growthTrend?.startsWith('+');
  const trendColor = trendPositive ? 'text-emerald-400' : 'text-red-400';

  // Memoize the date string so it doesn't cause footer re-renders
  const todayStr = useMemo(() => new Date().toLocaleDateString('en-LK'), []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-50 flex items-center justify-center pointer-events-auto bg-transparent"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.88, y: 30 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 180, damping: 22 }}
        className="relative w-full max-w-5xl mx-4 max-h-[92vh] bg-[#060a14]/95 backdrop-blur-3xl border border-cyan-500/25 rounded-3xl shadow-[0_0_80px_rgba(6,182,212,0.12)] overflow-hidden flex flex-col"
      >
        {/* Decorative scan lines */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(6,182,212,0.3) 2px, rgba(6,182,212,0.3) 4px)' }} />

        {/* ── Header ─────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-cyan-500/15 shrink-0">
          <div className="flex items-center gap-3">
            <motion.div animate={{ rotate: [0, 360] }} transition={{ duration: 6, repeat: Infinity, ease: 'linear' }}>
              <Activity className="text-cyan-400 w-5 h-5" />
            </motion.div>
            <div>
              <h2 className="text-sm font-black uppercase tracking-[0.25em] text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400">
                Luna Analytics HUD
              </h2>
              <p className="text-[9px] font-mono text-cyan-600/50 tracking-widest">
                {data?.store || 'STORE'} • LIVE TELEMETRY
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-black/40 border border-white/10 text-slate-400 text-[10px] font-bold hover:border-red-500/40 hover:text-red-400 transition-all"
          >
            <X size={12} /> CLOSE
          </button>
        </div>

        {/* ── Boot sequence / Content ────────────────────────── */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 custom-scrollbar">
          {!booted ? (
            <div className="flex flex-col items-center justify-center h-full min-h-[300px] gap-2">
              {BOOT_LINES.slice(0, bootIdx).map((line, i) => (
                <motion.p
                  key={`boot-${i}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="text-[11px] font-mono text-cyan-500/70 tracking-wider"
                >
                  {line}
                </motion.p>
              ))}
              <motion.div
                animate={{ opacity: [0.3, 1, 0.3] }}
                transition={{ duration: 1.2, repeat: Infinity }}
                className="mt-4 w-6 h-6 border-2 border-cyan-500/50 border-t-cyan-400 rounded-full animate-spin"
              />
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center h-full min-h-[300px]">
              <motion.div animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1, repeat: Infinity }} className="text-xs font-mono text-cyan-500/60">
                Streaming live data...
              </motion.div>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-full min-h-[300px] gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
                <Activity className="text-amber-400 w-5 h-5" />
              </div>
              <p className="text-xs font-mono text-slate-400">Analytics stream offline</p>
              <p className="text-[10px] font-mono text-slate-600 text-center max-w-[200px]">
                Ensure you are connected and authenticated as manager
              </p>
            </div>
          ) : data ? (
            <div className="flex flex-col gap-5">
              {/* ── KPI Cards ─────────────────────────────── */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <MetricCard
                  key="today-revenue"
                  icon={<DollarSign size={10} />}
                  label="Today's Revenue"
                  value={dailySummary ? `Rs.${dailySummary.todayRevenue.toLocaleString()}` : '—'}
                  sub="today"
                  delay={0.05}
                  accent="amber"
                />
                <MetricCard
                  key="alltime-revenue"
                  icon={<DollarSign size={10} />}
                  label="Total All-Time Revenue"
                  value={dailySummary ? `Rs.${dailySummary.totalAllTimeRevenue.toLocaleString()}` : '—'}
                  sub="lifetime"
                  delay={0.1}
                />
                <MetricCard
                  key="today-count"
                  icon={<BarChart3 size={10} />}
                  label="Today's Transactions"
                  value={dailySummary ? String(dailySummary.todaySalesCount) : '—'}
                  sub="bills today"
                  delay={0.15}
                  accent="cyan"
                />
                <MetricCard
                  key="today-profit"
                  icon={<DollarSign size={10} />}
                  label="Today's Net Profit"
                  value={dailySummary ? `Rs.${dailySummary.todayNetProfit.toLocaleString()}` : '—'}
                  sub="today"
                  delay={0.17}
                  accent={dailySummary && dailySummary.todayNetProfit >= 0 ? 'emerald' : 'red'}
                />
                <MetricCard
                  key="total-profit"
                  icon={<DollarSign size={10} />}
                  label="Total Net Profit"
                  value={dailySummary ? `Rs.${dailySummary.totalNetProfit.toLocaleString()}` : '—'}
                  sub="lifetime"
                  delay={0.19}
                  accent={dailySummary && dailySummary.totalNetProfit >= 0 ? 'emerald' : 'red'}
                />
                <MetricCard
                  key="revenue"
                  icon={<TrendingUp size={10} />}
                  label="Month Revenue"
                  value={`Rs.${data.currentMetrics.totalRevenue.toLocaleString()}`}
                  sub={`${data.currentMetrics.currency} this month`}
                  delay={0.2}
                />
                <MetricCard
                  key="trend"
                  icon={trendPositive ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                  label="Growth Trend"
                  value={data.growthTrend}
                  sub="vs previous week"
                  delay={0.25}
                  accent={trendPositive ? 'emerald' : 'red'}
                />
                <MetricCard
                  key="aov"
                  icon={<BarChart3 size={10} />}
                  label="Avg Order Value"
                  value={`Rs.${data.currentMetrics.averageOrderValue.toLocaleString()}`}
                  sub={`${data.currentMetrics.totalTransactions} transactions`}
                  delay={0.3}
                />
                <MetricCard
                  key="category"
                  icon={<ShoppingBag size={10} />}
                  label="Top Category"
                  value={data.currentMetrics.topSellingCategory}
                  sub="by revenue"
                  delay={0.35}
                />
              </div>

              {/* ── Chart ─────────────────────────────────── */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, type: 'spring', stiffness: 140 }}
                className="bg-gradient-to-br from-cyan-950/30 to-black/40 border border-cyan-500/15 rounded-2xl p-4 sm:p-5"
              >
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[9px] uppercase font-mono text-cyan-500/50 tracking-[0.2em]">Daily Revenue + 7-Day Forecast</p>
                  <div className="flex items-center gap-1.5">
                    <span className={`text-xs font-bold ${trendColor} flex items-center gap-1`}>
                      {trendPositive ? <TrendingUp size={12} /> : <TrendingDown size={12} />} {data.growthTrend}
                    </span>
                  </div>
                </div>
                <div className="w-full" style={{ height: '200px' }}>
                  <AnalyticsChart data={data} />
                </div>
              </motion.div>

              {/* ── Luna Insight ──────────────────────────── */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="bg-gradient-to-r from-purple-950/30 to-cyan-950/20 border border-purple-500/20 rounded-2xl p-4"
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-purple-500/20 border border-purple-500/30 flex items-center justify-center shrink-0 mt-0.5">
                    <Zap size={14} className="text-purple-400" />
                  </div>
                  <div>
                    <p className="text-[9px] uppercase font-mono text-purple-500/50 tracking-[0.2em] mb-1">Luna Insight</p>
                    <p className="text-sm text-slate-300 leading-relaxed">
                      Revenue is <span className={`font-bold ${trendColor}`}>{trendPositive ? 'up' : 'down'} by {data.growthTrend}</span> compared to last week.
                      {' '}Your top performer is <span className="font-bold text-cyan-300">{data.currentMetrics.topSellingCategory}</span> with an average order value of <span className="font-bold text-cyan-300">Rs.{data.currentMetrics.averageOrderValue.toLocaleString()}</span>.
                      {trendPositive
                        ? ` Momentum looks strong — consider restocking ${data.currentMetrics.topSellingCategory} products to capitalize on demand.`
                        : ` Consider running a promotion on ${data.currentMetrics.topSellingCategory} to boost the numbers.`
                      }
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* ── Weekly Trends Bar Chart (manager only) ─── */}
              {trendsData && trendsData.bestDay && (
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.85, type: 'spring', stiffness: 130 }}
                  className="bg-gradient-to-br from-slate-900/60 to-black/40 border border-cyan-500/15 rounded-2xl p-4 sm:p-5"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-[9px] uppercase font-mono text-cyan-500/50 tracking-[0.2em]">Weekly Sales Pattern</p>
                      <p className="text-[8px] font-mono text-slate-600 mt-0.5">{trendsData.totalTransactions} lifetime transactions</p>
                    </div>
                    <div className="flex items-center gap-3 text-[8px] font-mono">
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-sm bg-emerald-500/80 inline-block"/>
                        Best: <span className="text-emerald-400 font-bold">{trendsData.bestDay}</span>
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-sm bg-red-500/70 inline-block"/>
                        Slow: <span className="text-red-400">{trendsData.worstDay}</span>
                      </span>
                    </div>
                  </div>
                  <div className="w-full" style={{ height: '130px' }}>
                    <WeeklyTrendsBar data={trendsData} />
                  </div>
                </motion.div>
              )}
            </div>
          ) : null}
        </div>

        {/* ── Footer ─────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-5 py-2.5 border-t border-cyan-500/10 text-[8px] font-mono text-cyan-800/40 tracking-widest shrink-0">
          <span>LUNA ANALYTICS v1.0</span>
          <span>{todayStr}</span>
        </div>
      </motion.div>
    </motion.div>
  );
}
